import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import { VehicleDto } from "./VehicleView";
import {
  showSuccessAlert,
  showError,
  showConfirmSubmission,
} from "../../../../../core/alert/sweetAlertConfig";

// Define interfaces for type safety
interface VehicleType {
  id: number;
  type: string;
}

interface VehicleFeature {
  id: number;
  featureName: string;
}

interface BrandData {
  data: {
    vehicleBrands: { id: number; name: string; models: { id: number; name: string }[] }[];
  };
}

interface Config {
  conditions: string[];
  states: string[];
  gearTypes: string[];
  fuelTypes: { id: number; name: string }[];
  vehicleClasses: string[];
}

interface EditFormData extends VehicleDto {
  lastGeneralServiceDate: string | null;
  lastAdvancedServiceDate: string | null;
  lastTyreChangeDate: string | null;
}

interface VehicleEditProps {
  show: boolean;
  onHide: () => void;
  vehicle: VehicleDto;
  onSave: (updatedVehicle: VehicleDto) => void;
}

interface ApiResponse<T> {
  status: string;
  statusDescription: string;
  data: T | null;
}

// Convert EditFormData to VehicleDto
const toVehicleDto = (formData: EditFormData, vehicle: VehicleDto): VehicleDto => ({
  ...vehicle,
  ...formData,
  vehicleId: formData.vehicleId || "",
  numberPlate: formData.numberPlate || "",
  vehicleTypeId: formData.vehicleTypeId || 0,
  vehicleType: formData.vehicleType || "",
  brandId: formData.brandId || 0,
  vehicleBrand: formData.vehicleBrand || null,
  modelId: formData.modelId || 0,
  vehicleModel: formData.vehicleModel || null,
  vehicleClassId: formData.vehicleClassId || 0,
  vehicleClass: formData.vehicleClass || null,
  conditionId: formData.conditionId || 0,
  vehicleCondition: formData.vehicleCondition || null,
  gearTypeId: formData.gearTypeId || 0,
  vehicleGearType: formData.vehicleGearType || null,
  featureIds: formData.featureIds || [],
  vehicleFeatures: formData.vehicleFeatures || [],
  fuelTypeId: formData.fuelTypeId || 0,
  vehicleFuelType: formData.vehicleFuelType || null,
  statusId: formData.statusId || 0,
  vehicleStatus: formData.vehicleStatus || null,
  engineCapacity: formData.engineCapacity ?? null,
  batteryCapacity: formData.batteryCapacity ?? null,
  ownerName: formData.ownerName || null,
  ownerAddress: formData.ownerAddress || null,
  noOfPassengers: formData.noOfPassengers ?? null,
  manufacturingYear: formData.manufacturingYear ?? null,
  chassisNo: formData.chassisNo || null,
  engineNo: formData.engineNo || null,
  grossWeight: formData.grossWeight ?? null,
  value: formData.value ?? null,
  noOfKms: formData.noOfKms ?? null,
  inStationEconomy: formData.inStationEconomy ?? null,
  outStationEconomy: formData.outStationEconomy ?? null,
  rentPerKm: formData.rentPerKm ?? null,
  tyreSize: formData.tyreSize || null,
  frontTyreSize: formData.frontTyreSize || null,
  rearTyreSize: formData.rearTyreSize || null,
  color: formData.color || null,
  generalServiceStandardKms: formData.generalServiceStandardKms ?? null,
  lastGeneralServiceKms: formData.lastGeneralServiceKms ?? null,
  advancedServiceStandardKms: formData.advancedServiceStandardKms ?? null,
  lastAdvancedServiceKms: formData.lastAdvancedServiceKms ?? null,
  standardKmsForTyreChange: formData.standardKmsForTyreChange ?? null,
  lastTyreChangeKms: formData.lastTyreChangeKms ?? null,
  lastGeneralServiceDate: formData.lastGeneralServiceDate || null,
  lastAdvancedServiceDate: formData.lastAdvancedServiceDate || null,
  lastTyreChangeDate: formData.lastTyreChangeDate || null,
  images: formData.images || [],
  bookImages: formData.bookImages || [],
  createdDate: formData.createdDate || null,
  createdBy: formData.createdBy || null,
});

// Format date as YYYY-MM-DDTHH:mm:ss+05:30
const formatDateTime = (date: string | null): string | null => {
  if (!date) return null;
  try {
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return null;
    return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}-${String(
      parsedDate.getDate()
    ).padStart(2, "0")}T00:00:00+05:30`;
  } catch {
    return null;
  }
};

const VehicleEdit: React.FC<VehicleEditProps> = ({ show, onHide, vehicle, onSave }) => {
  const [formData, setFormData] = useState<EditFormData>({
    vehicleId: vehicle.vehicleId || "",
    vehicleTypeId: vehicle.vehicleTypeId || 0,
    vehicleType: vehicle.vehicleType || null,
    brandId: vehicle.brandId || 0,
    vehicleBrand: vehicle.vehicleBrand || null,
    modelId: vehicle.modelId || 0,
    vehicleModel: vehicle.vehicleModel || null,
    vehicleClassId: vehicle.vehicleClassId || 0,
    vehicleClass: vehicle.vehicleClass || null,
    conditionId: vehicle.conditionId || 0,
    vehicleCondition: vehicle.vehicleCondition || null,
    gearTypeId: vehicle.gearTypeId || 0,
    vehicleGearType: vehicle.vehicleGearType || null,
    featureIds: vehicle.featureIds || [],
    vehicleFeatures: vehicle.vehicleFeatures || null,
    fuelTypeId: vehicle.fuelTypeId || 0,
    vehicleFuelType: vehicle.vehicleFuelType || null,
    engineCapacity: vehicle.engineCapacity ?? null,
    batteryCapacity: vehicle.batteryCapacity ?? null,
    numberPlate: vehicle.numberPlate || "",
    ownerName: vehicle.ownerName || null,
    ownerAddress: vehicle.ownerAddress || null,
    noOfPassengers: vehicle.noOfPassengers ?? null,
  assignedParkingSlotName: vehicle.assignedParkingSlotName || null,
    manufacturingYear: vehicle.manufacturingYear ?? null,
    chassisNo: vehicle.chassisNo || null,
    engineNo: vehicle.engineNo || null,
    grossWeight: vehicle.grossWeight ?? null,
    value: vehicle.value ?? null,
    noOfKms: vehicle.noOfKms ?? null,
    inStationEconomy: vehicle.inStationEconomy ?? null,
    outStationEconomy: vehicle.outStationEconomy ?? null,
    rentPerKm: vehicle.rentPerKm ?? null,
    tyreSize: vehicle.tyreSize || null,
    frontTyreSize: vehicle.frontTyreSize || null,
    rearTyreSize: vehicle.rearTyreSize || null,
    color: vehicle.color || null,
    generalServiceStandardKms: vehicle.generalServiceStandardKms ?? null,
    lastGeneralServiceKms: vehicle.lastGeneralServiceKms ?? null,
    advancedServiceStandardKms: vehicle.advancedServiceStandardKms ?? null,
    lastAdvancedServiceKms: vehicle.lastAdvancedServiceKms ?? null,
    standardKmsForTyreChange: vehicle.standardKmsForTyreChange ?? null,
    lastTyreChangeKms: vehicle.lastTyreChangeKms ?? null,
    statusId: vehicle.statusId || 0,
    vehicleStatus: vehicle.vehicleStatus || null,
    lastGeneralServiceDate: vehicle.lastGeneralServiceDate || null,
    lastAdvancedServiceDate: vehicle.lastAdvancedServiceDate || null,
    lastTyreChangeDate: vehicle.lastTyreChangeDate || null,
    images: vehicle.images || [],
    bookImages: vehicle.bookImages || [],
    createdDate: vehicle.createdDate || null,
    createdBy: vehicle.createdBy || null,
  });
  const [vehicleImages, setVehicleImages] = useState<File[]>([]);
  const [registrationBookImages, setRegistrationBookImages] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isConfigLoading, setIsConfigLoading] = useState<boolean>(false);
  const [hasUserChanges, setHasUserChanges] = useState<boolean>(false);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [features, setFeatures] = useState<VehicleFeature[]>([]);
  const [brandData, setBrandData] = useState<BrandData | null>(null);
  const [config, setConfig] = useState<Config>({
    conditions: [],
    states: [],
    gearTypes: [],
    fuelTypes: [],
    vehicleClasses: [],
  });

  // Synchronize formData with configuration data
  useEffect(() => {
    if (!show || isConfigLoading || !vehicleTypes.length || !brandData || !config.vehicleClasses.length) return;

    setFormData((prev) => {
      const newFormData = { ...prev };

      const matchedVehicleType = vehicleTypes.find((vt) => vt.type === prev.vehicleType);
      newFormData.vehicleTypeId = matchedVehicleType?.id || prev.vehicleTypeId || 0;
      newFormData.vehicleType = matchedVehicleType?.type || prev.vehicleType || null;

      const matchedBrand = brandData.data.vehicleBrands.find((b) => b.name === prev.vehicleBrand);
      newFormData.brandId = matchedBrand?.id || prev.brandId || 0;
      newFormData.vehicleBrand = matchedBrand?.name || prev.vehicleBrand || null;

      const matchedModel = matchedBrand?.models.find((m) => m.name === prev.vehicleModel);
      newFormData.modelId = matchedModel?.id || prev.modelId || 0;
      newFormData.vehicleModel = matchedModel?.name || prev.vehicleModel || null;

      const matchedClassIndex = config.vehicleClasses.findIndex((vc) => vc === prev.vehicleClass);
      newFormData.vehicleClassId = matchedClassIndex >= 0 ? matchedClassIndex + 1 : prev.vehicleClassId || 0;
      newFormData.vehicleClass = config.vehicleClasses[matchedClassIndex] || prev.vehicleClass || null;

      const matchedConditionIndex = config.conditions.findIndex((c) => c === prev.vehicleCondition);
      newFormData.conditionId = matchedConditionIndex >= 0 ? matchedConditionIndex + 1 : prev.conditionId || 0;
      newFormData.vehicleCondition = config.conditions[matchedConditionIndex] || prev.vehicleCondition || null;

      const matchedGearTypeIndex = config.gearTypes.findIndex((g) => g === prev.vehicleGearType);
      newFormData.gearTypeId = matchedGearTypeIndex >= 0 ? matchedGearTypeIndex + 1 : prev.gearTypeId || 0;
      newFormData.vehicleGearType = config.gearTypes[matchedGearTypeIndex] || prev.vehicleGearType || null;

      const matchedFuelType = config.fuelTypes.find((ft) => ft.name === prev.vehicleFuelType);
      newFormData.fuelTypeId = matchedFuelType?.id || prev.fuelTypeId || 0;
      newFormData.vehicleFuelType = matchedFuelType?.name || prev.vehicleFuelType || null;

      const matchedStatusIndex = config.states.findIndex((s) => s === prev.vehicleStatus);
      newFormData.statusId = matchedStatusIndex >= 0 ? matchedStatusIndex + 1 : prev.statusId || 0;
      newFormData.vehicleStatus = config.states[matchedStatusIndex] || prev.vehicleStatus || null;

      const matchedFeatures = prev.vehicleFeatures
        ?.map((f) => features.find((feature) => feature.featureName === f)?.id)
        .filter((id): id is number => id !== undefined) || [];
      newFormData.featureIds = matchedFeatures.length > 0 ? matchedFeatures : prev.featureIds || [];

      return newFormData;
    });
  }, [show, isConfigLoading, vehicleTypes, brandData, config, vehicle]);

  // Fetch configuration data
  useEffect(() => {
    const fetchData = async () => {
      setIsConfigLoading(true);
      try {
        const [vehicleTypesRes, featuresRes, configRes, brandsRes] = await Promise.all([
          apiDefinitions.getAllVehicleTypes(),
          apiDefinitions.getAllVehicleFeatures(),
          apiDefinitions.loadVehicleConfig(),
          apiDefinitions.getVehicleBrandModels("1", {}),
        ]);

        if (vehicleTypesRes.data.status === "S1000" && Array.isArray(vehicleTypesRes.data.data)) {
          setVehicleTypes(vehicleTypesRes.data.data);
        } else {
          throw new Error(vehicleTypesRes.data.statusDescription || "Failed to fetch vehicle types");
        }

        if (featuresRes.data.status === "S1000" && Array.isArray(featuresRes.data.data)) {
          setFeatures(featuresRes.data.data);
        } else {
          throw new Error(featuresRes.data.statusDescription || "Failed to load features");
        }

        if (configRes.data.status === "S1000" && configRes.data.data) {
          setConfig({
            conditions: configRes.data.data.conditions || [],
            states: configRes.data.data.states || [],
            gearTypes: configRes.data.data.gearTypes || [],
            fuelTypes: configRes.data.data.fuelTypes.map((ft: string, index: number) => ({
              id: index + 1,
              name: ft,
            })),
            vehicleClasses: configRes.data.data.vehicleClasses || [],
          });
        } else {
          throw new Error(configRes.data.statusDescription || "Failed to load configuration");
        }

        if (brandsRes.data.status === "S1000" && brandsRes.data.data?.vehicleBrands) {
          setBrandData(brandsRes.data);
        } else {
          throw new Error(brandsRes.data.statusDescription || "Failed to load brands");
        }
      } catch (error: any) {
        setError(error.message || "An error occurred fetching configuration data");
      } finally {
        setIsConfigLoading(false);
      }
    };

    if (show) {
      fetchData();
    }
  }, [show]);

  const handleFeatureChange = (featureId: number, checked: boolean) => {
    setHasUserChanges(true);
    setFormData((prev) => {
      const featureIds = checked
        ? [...(prev.featureIds || []), featureId]
        : (prev.featureIds || []).filter((id: number) => id !== featureId);
      const vehicleFeatures = featureIds
        .map((id: number) => features.find((f) => f.id === id)?.featureName)
        .filter((f: string | undefined): f is string => !!f);
      return { ...prev, featureIds, vehicleFeatures };
    });
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    maxSizeMB: number = 5,
    allowedTypes: string[] = ["image/jpeg", "image/png", "image/jpg"]
  ) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validFiles = files.filter((file) => {
        const isValidType = allowedTypes.includes(file.type);
        const isValidSize = file.size <= maxSizeMB * 1024 * 1024;
        if (!isValidType) {
          showError(`Invalid file type: ${file.name}. Allowed types: ${allowedTypes.join(", ")}`);
        }
        if (!isValidSize) {
          showError(`File too large: ${file.name}. Max size: ${maxSizeMB}MB`);
        }
        return isValidType && isValidSize;
      });
      setFiles(validFiles);
    }
  };

  const validateVehicleForm = () => {
    const isBike = formData.vehicleType?.toLowerCase().includes("motorcycle") || formData.vehicleType?.toLowerCase().includes("bike");
    const isElectric = formData.vehicleFuelType?.toLowerCase() === "electric";
    const tyreSizeRegex = /^\d{2,3}\/\d{1,2}-\d{1,2}$/;


    if (!formData.numberPlate.trim()) {
      setError("Number plate is required");
      return false;
    }
    if (!formData.vehicleTypeId || formData.vehicleTypeId <= 0) {
      setError("Valid vehicle type is required");
      return false;
    }
    if (!formData.brandId || formData.brandId <= 0) {
      setError("Valid brand is required");
      return false;
    }
    if (!formData.modelId || formData.modelId <= 0) {
      setError("Valid model is required");
      return false;
    }
    if (!formData.vehicleClassId || formData.vehicleClassId <= 0) {
      setError("Valid vehicle class is required");
      return false;
    }
    if (!formData.conditionId || formData.conditionId <= 0) {
      setError("Valid condition is required");
      return false;
    }
    if (!formData.gearTypeId || formData.gearTypeId <= 0) {
      setError("Valid gear type is required");
      return false;
    }
    if (!formData.fuelTypeId || formData.fuelTypeId <= 0) {
      setError("Valid fuel type is required");
      return false;
    }
    if (!formData.statusId || formData.statusId <= 0) {
      setError("Valid status is required");
      return false;
    }
    if (isBike && (!formData.frontTyreSize?.trim() || !formData.rearTyreSize?.trim())) {
      setError("Front and rear tyre sizes are required for bikes");
      return false;
    }
    if (isBike && formData.frontTyreSize && !tyreSizeRegex.test(formData.frontTyreSize)) {
      setError("Invalid front tyre size format (e.g., 250/11-45)");
      return false;
    }
    if (isBike && formData.rearTyreSize && !tyreSizeRegex.test(formData.rearTyreSize)) {
      setError("Invalid rear tyre size format (e.g., 250/11-45)");
      return false;
    }
    if (!isBike && !formData.tyreSize?.trim()) {
      setError("Tyre size is required for non-bike vehicles");
      return false;
    }
    if (!isBike && formData.tyreSize && !tyreSizeRegex.test(formData.tyreSize)) {
      setError("Invalid tyre size format (e.g., 185/65-15)");
      return false;
    }
    if (isElectric) {
      if (!formData.batteryCapacity || formData.batteryCapacity <= 0) {
        setError("Battery capacity must be positive for electric vehicles");
        return false;
      }
    } else {
      if (!formData.engineCapacity || formData.engineCapacity <= 0) {
        setError("Engine capacity must be positive for non-electric vehicles");
        return false;
      }
      if (!formData.engineNo?.trim()) {
        setError("Engine number is required for non-electric vehicles");
        return false;
      }
      if (!formData.inStationEconomy || formData.inStationEconomy <= 0) {
        setError("In-station economy must be positive for non-electric vehicles");
        return false;
      }
      if (!formData.outStationEconomy || formData.outStationEconomy <= 0) {
        setError("Out-station economy must be positive for non-electric vehicles");
        return false;
      }
    }
    if (!formData.noOfPassengers || formData.noOfPassengers <= 0) {
      setError("Number of passengers must be positive");
      return false;
    }
    if (!formData.manufacturingYear || formData.manufacturingYear <= 0) {
      setError("Manufacturing year must be positive");
      return false;
    }
    if (!formData.chassisNo?.trim()) {
      setError("Chassis number is required");
      return false;
    }
    if (!formData.value || formData.value <= 0) {
      setError("Vehicle value must be positive");
      return false;
    }
    if (!formData.noOfKms || formData.noOfKms <= 0) {
      setError("Number of kilometers must be positive");
      return false;
    }
    if (!formData.color?.trim()) {
      setError("Color is required");
      return false;
    }
    if (!formData.generalServiceStandardKms || formData.generalServiceStandardKms <= 0) {
      setError("General service standard kilometers must be positive");
      return false;
    }
    if (formData.lastGeneralServiceKms && formData.lastGeneralServiceKms > formData.noOfKms) {
      setError("Last general service kilometers cannot exceed total kilometers");
      return false;
    }
    if (!formData.advancedServiceStandardKms || formData.advancedServiceStandardKms <= 0) {
      setError("Advanced service standard kilometers must be positive");
      return false;
    }
    if (formData.lastAdvancedServiceKms && formData.lastAdvancedServiceKms > formData.noOfKms) {
      setError("Last advanced service kilometers cannot exceed total kilometers");
      return false;
    }
    if (!formData.standardKmsForTyreChange || formData.standardKmsForTyreChange <= 0) {
      setError("Standard kilometers for tyre change must be positive");
      return false;
    }
    if (formData.lastTyreChangeKms && formData.lastTyreChangeKms > formData.noOfKms) {
      setError("Last tyre change kilometers cannot exceed total kilometers");
      return false;
    }
    return true;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    setData: React.Dispatch<React.SetStateAction<any>> = setFormData,
    data: any = formData
  ) => {
    const { name, value } = e.target;
    setHasUserChanges(true);
    setData((prev: any) => {
      const isElectric = name === "fuelTypeId" && config.fuelTypes.find((ft) => ft.id === parseInt(value))?.name.toLowerCase() === "electric";
      const newData = {
        ...prev,
        [name]:
          name.includes("Kms") ||
          name.includes("Year") ||
          name.includes("Capacity") ||
          name.includes("Weight") ||
          name.includes("Passengers") ||
          name.includes("Value") ||
          name.includes("Economy") ||
          name.includes("Rent") ||
          name.includes("Id")
            ? value === "" ? null : Number(value) || 0
            : value || null,
      };

      if (setData === setFormData) {
        if (name === "vehicleTypeId") {
          const selectedType = vehicleTypes.find((vt) => vt.id === parseInt(value));
          newData.vehicleType = selectedType?.type || null;
        } else if (name === "brandId") {
          const selectedBrand = brandData?.data.vehicleBrands.find((b) => b.id === parseInt(value));
          newData.vehicleBrand = selectedBrand?.name || null;
          newData.modelId = null;
          newData.vehicleModel = null;
        } else if (name === "modelId") {
          const selectedBrand = brandData?.data.vehicleBrands.find((b) => b.id === prev.brandId);
          const selectedModel = selectedBrand?.models.find((m) => m.id === parseInt(value));
          newData.vehicleModel = selectedModel?.name || null;
        } else if (name === "vehicleClassId") {
          newData.vehicleClass = config.vehicleClasses[parseInt(value) - 1] || null;
        } else if (name === "conditionId") {
          newData.vehicleCondition = config.conditions[parseInt(value) - 1] || null;
        } else if (name === "gearTypeId") {
          newData.vehicleGearType = config.gearTypes[parseInt(value) - 1] || null;
        } else if (name === "fuelTypeId") {
          const fuelTypeObj = config.fuelTypes.find((ft) => ft.id === parseInt(value));
          newData.vehicleFuelType = fuelTypeObj?.name || null;
          if (isElectric) {
            newData.engineCapacity = null;
            newData.engineNo = null;
            newData.inStationEconomy = null;
            newData.outStationEconomy = null;
          } else {
            newData.batteryCapacity = null;
          }
        } else if (name === "statusId") {
          newData.vehicleStatus = config.states[parseInt(value) - 1] || null;
        }
      }
      return newData;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateVehicleForm()) {
      return;
    }

    const isConfirmed = await showConfirmSubmission(0);
    if (!isConfirmed) return;

    try {
      setLoading(true);
      const isElectric = formData.vehicleFuelType?.toLowerCase() === "electric";
      const isBike = formData.vehicleType?.toLowerCase().includes("motorcycle") || formData.vehicleType?.toLowerCase().includes("bike");

      const vehiclePayload = {
        numberPlate: formData.numberPlate.toUpperCase(),
        vehicleTypeId: {
          id: formData.vehicleTypeId || 0,
          type: formData.vehicleType || "",
        },
        brandId: formData.brandId || 0,
        modelId: formData.modelId || 0,
        vehicleClassId: formData.vehicleClassId || 0,
        conditionId: formData.conditionId || 0,
        gearTypeId: formData.gearTypeId || 0,
        fuelTypeId: formData.fuelTypeId || 0,
        statusId: formData.statusId || 0,
        featureIds: formData.featureIds || [],
        engineCapacity: isElectric ? null : formData.engineCapacity || null,
        batteryCapacity: isElectric ? formData.batteryCapacity || null : null,
        ownerName: formData.ownerName || null,
        ownerAddress: formData.ownerAddress || null,
        noOfPassengers: formData.noOfPassengers || null,
        manufacturingYear: formData.manufacturingYear || null,
        chassisNo: formData.chassisNo || null,
        engineNo: isElectric ? null : formData.engineNo || null,
        grossWeight: formData.grossWeight || null,
        value: formData.value || null,
        noOfKms: formData.noOfKms || null,
        inStationEconomy: isElectric ? null : formData.inStationEconomy || null,
        outStationEconomy: isElectric ? null : formData.outStationEconomy || null,
        rentPerKm: formData.rentPerKm || null,
        tyreSize: isBike ? null : formData.tyreSize || null,
        frontTyreSize: isBike ? formData.frontTyreSize || null : null,
        rearTyreSize: isBike ? formData.rearTyreSize || null : null,
        color: formData.color || null,
        generalServiceStandardKms: formData.generalServiceStandardKms || null,
        lastGeneralServiceKms: formData.lastGeneralServiceKms || null,
        advancedServiceStandardKms: formData.advancedServiceStandardKms || null,
        lastAdvancedServiceKms: formData.lastAdvancedServiceKms || null,
        standardKmsForTyreChange: formData.standardKmsForTyreChange || null,
        lastTyreChangeKms: formData.lastTyreChangeKms || null,
        lastGeneralServiceDate: formatDateTime(formData.lastGeneralServiceDate),
        lastAdvancedServiceDate: formatDateTime(formData.lastAdvancedServiceDate),
        lastTyreChangeDate: formatDateTime(formData.lastTyreChangeDate),
      };

      const vehicleFormData = new FormData();
      vehicleImages.forEach((file) => vehicleFormData.append("images", file));
      const regBookFormData = new FormData();
      registrationBookImages.forEach((file) => regBookFormData.append("images", file));


      const [vehicleRes, vehicleImageRes, regBookImageRes] = await Promise.all([
        apiDefinitions.updateVehicle(vehiclePayload),
        vehicleImages.length > 0 ? apiDefinitions.uploadVehicleImages(formData.numberPlate, vehicleFormData) : Promise.resolve({ data: { status: "S1000" } }),
        registrationBookImages.length > 0
          ? apiDefinitions.uploadRegistrationBookImages(formData.numberPlate, regBookFormData)
          : Promise.resolve({ data: { status: "S1000" } }),
      ]);

      if (
        vehicleRes.data.status === "S1000" &&
        vehicleImageRes.data.status === "S1000" &&
        regBookImageRes.data.status === "S1000"
      ) {
        showSuccessAlert("Vehicle updated successfully");
        onSave(toVehicleDto(formData, vehicle));
        setError(null);
        onHide();
      } else {
        const errors = [
          vehicleRes.data.statusDescription,
          vehicleImageRes.data.statusDescription,
          regBookImageRes.data.statusDescription,
        ].filter(Boolean).join("; ");
        setError(errors || "Failed to update vehicle");
      }
    } catch (err: any) {
      const errorDetails = err.response?.data?.errors
        ? Object.entries(err.response.data.errors)
            .map(([field, message]) => `${field}: ${message}`)
            .join("; ")
        : err.response?.data?.statusDescription || err.message || "Unknown error";
      setError(`Error updating vehicle: ${errorDetails}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    if (vehicle.numberPlate) {
      fetchVehicleData(vehicle.numberPlate);
    }
  };

  const fetchVehicleData = async (numberPlate: string) => {
    try {
      setLoading(true);
      const response = await apiDefinitions.getVehicleByNumberPlate(numberPlate);
      const data: ApiResponse<VehicleDto> = response.data;
      if (data.status === "S1000" && data.data) {
        const vehicleData = data.data;
        setFormData((prev) => ({
          ...prev,
          ...vehicleData,
          lastGeneralServiceDate: vehicleData.lastGeneralServiceDate || null,
          lastAdvancedServiceDate: vehicleData.lastAdvancedServiceDate || null,
          lastTyreChangeDate: vehicleData.lastTyreChangeDate || null,
        }));
        setError(null);
      } else {
        setError(data.statusDescription || "Failed to fetch vehicle data");
      }
    } catch (err: any) {
      setError(`Error fetching vehicle: ${err.response?.data?.statusDescription || err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const isBike = formData.vehicleType?.toLowerCase().includes("motorcycle") || formData.vehicleType?.toLowerCase().includes("bike");
  const isElectric = formData.vehicleFuelType?.toLowerCase() === "electric";

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Edit Vehicle - {formData.numberPlate || "Loading..."}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading || isConfigLoading ? (
          <div className="text-center">
            <Spinner animation="border" />
            <p>Loading vehicle data...</p>
          </div>
        ) : null}
        {error && (
          <div className="alert alert-danger">
            {error}
            <button className="btn btn-link" onClick={handleRetry}>
              Retry
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-4">
              <h5>Vehicle Details</h5>
              <div className="mb-3">
                <label className="form-label">
                  Number Plate <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="numberPlate"
                  value={formData.numberPlate || ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Vehicle Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="vehicleTypeId"
                  value={formData.vehicleTypeId || ""}
                  onChange={handleChange}
                  disabled={isConfigLoading}
                  required
                >
                  <option value="">Select Vehicle Type</option>
                  {vehicleTypes.map((vt) => (
                    <option key={vt.id} value={vt.id}>
                      {vt.type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Brand <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="brandId"
                  value={formData.brandId || ""}
                  onChange={handleChange}
                  disabled={isConfigLoading}
                  required
                >
                  <option value="">Select Brand</option>
                  {brandData?.data.vehicleBrands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Model <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="modelId"
                  value={formData.modelId || ""}
                  onChange={handleChange}
                  disabled={isConfigLoading || !formData.brandId}
                  required
                >
                  <option value="">Select Model</option>
                  {brandData?.data.vehicleBrands
                    .find((b) => b.id === formData.brandId)
                    ?.models.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Class <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="vehicleClassId"
                  value={formData.vehicleClassId || ""}
                  onChange={handleChange}
                  disabled={isConfigLoading}
                  required
                >
                  <option value="">Select Class</option>
                  {config.vehicleClasses.map((vc, index) => (
                    <option key={index + 1} value={index + 1}>
                      {vc}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Condition <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="conditionId"
                  value={formData.conditionId || ""}
                  onChange={handleChange}
                  disabled={isConfigLoading}
                  required
                >
                  <option value="">Select Condition</option>
                  {config.conditions.map((c, index) => (
                    <option key={index + 1} value={index + 1}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Gear Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="gearTypeId"
                  value={formData.gearTypeId || ""}
                  onChange={handleChange}
                  disabled={isConfigLoading}
                  required
                >
                  <option value="">Select Gear Type</option>
                  {config.gearTypes.map((g, index) => (
                    <option key={index + 1} value={index + 1}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Fuel Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="fuelTypeId"
                  value={formData.fuelTypeId || ""}
                  onChange={handleChange}
                  disabled={isConfigLoading}
                  required
                >
                  <option value="">Select Fuel Type</option>
                  {config.fuelTypes.map((ft) => (
                    <option key={ft.id} value={ft.id}>
                      {ft.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Status <span className="text-danger">*</span>
                </label>
                <select
                  className="form-control"
                  name="statusId"
                  value={formData.statusId || ""}
                  onChange={handleChange}
                  disabled={isConfigLoading}
                  required
                >
                  <option value="">Select Status</option>
                  {config.states.map((s, index) => (
                    <option key={index + 1} value={index + 1}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Features</label>
                <div>
                  {features.map((f) => (
                    <div key={f.id} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.featureIds?.includes(f.id) || false}
                        onChange={(e) => handleFeatureChange(f.id, e.target.checked)}
                        disabled={isConfigLoading}
                      />
                      <label className="form-check-label">{f.featureName}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Vehicle Images</label>
                <input
                  type="file"
                  className="form-control"
                  multiple
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileChange(e, setVehicleImages)}
                />
                {vehicleImages.length > 0 && (
                  <ul>
                    {vehicleImages.map((file, index) => (
                      <li key={index}>{file.name}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Registration Book Images</label>
                <input
                  type="file"
                  className="form-control"
                  multiple
                  accept="image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleFileChange(e, setRegistrationBookImages)}
                />
                {registrationBookImages.length > 0 && (
                  <ul>
                    {registrationBookImages.map((file, index) => (
                      <li key={index}>{file.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <h5>Additional Details</h5>
              <div className="mb-3">
                <label className="form-label">Owner Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="ownerName"
                  value={formData.ownerName || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Owner Address</label>
                <textarea
                  className="form-control"
                  name="ownerAddress"
                  value={formData.ownerAddress || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  No. of Passengers <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="noOfPassengers"
                  value={formData.noOfPassengers ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Manufacturing Year <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="manufacturingYear"
                  value={formData.manufacturingYear ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Chassis No <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="chassisNo"
                  value={formData.chassisNo || ""}
                  onChange={handleChange}
                  required
                />
              </div>
              {!isElectric && (
                <div className="mb-3">
                  <label className="form-label">
                    Engine No <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="engineNo"
                    value={formData.engineNo || ""}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Gross Weight (kg)</label>
                <input
                  type="number"
                  className="form-control"
                  name="grossWeight"
                  value={formData.grossWeight ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Value (Rs) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="value"
                  value={formData.value ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  No. of Kilometers <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="noOfKms"
                  value={formData.noOfKms ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              {!isElectric && (
                <>
                  <div className="mb-3">
                    <label className="form-label">
                      In-Station Economy (km/l) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control"
                      name="inStationEconomy"
                      value={formData.inStationEconomy ?? ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Out-Station Economy (km/l) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      className="form-control"
                      name="outStationEconomy"
                      value={formData.outStationEconomy ?? ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </>
              )}
              {isElectric && (
                <div className="mb-3">
                  <label className="form-label">
                    Battery Capacity (kWh) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    className="form-control"
                    name="batteryCapacity"
                    value={formData.batteryCapacity ?? ""}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">Rent Per Kilometer</label>
                <input
                  type="number"
                  className="form-control"
                  name="rentPerKm"
                  value={formData.rentPerKm ?? ""}
                  onChange={handleChange}
                />
              </div>
              {isBike ? (
                <>
                  <div className="mb-3">
                    <label className="form-label">
                      Front Tyre Size <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="frontTyreSize"
                      value={formData.frontTyreSize || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Rear Tyre Size <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="rearTyreSize"
                      value={formData.rearTyreSize || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </>
              ) : (
                <div className="mb-3">
                  <label className="form-label">
                    Tyre Size <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="tyreSize"
                    value={formData.tyreSize || ""}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}
              <div className="mb-3">
                <label className="form-label">
                  Color <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="color"
                  value={formData.color || ""}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="col-md-4">
              <h5>Service and Maintenance</h5>
              <div className="mb-3">
                <label className="form-label">
                  General Service Standard (km) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="generalServiceStandardKms"
                  value={formData.generalServiceStandardKms ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Last General Service (km) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="lastGeneralServiceKms"
                  value={formData.lastGeneralServiceKms ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Last General Service Date</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  name="lastGeneralServiceDate"
                  value={formData.lastGeneralServiceDate || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Advanced Service Standard (km) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="advancedServiceStandardKms"
                  value={formData.advancedServiceStandardKms ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Last Advanced Service (km) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="lastAdvancedServiceKms"
                  value={formData.lastAdvancedServiceKms ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Last Advanced Service Date</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  name="lastAdvancedServiceDate"
                  value={formData.lastAdvancedServiceDate || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Standard Kms for Tyre Change <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="standardKmsForTyreChange"
                  value={formData.standardKmsForTyreChange ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  Last Tyre Change (km) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="lastTyreChangeKms"
                  value={formData.lastTyreChangeKms ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Last Tyre Change Date</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  name="lastTyreChangeDate"
                  value={formData.lastTyreChangeDate || ""}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          <Modal.Footer>
            <Button variant="primary" onClick={onHide} disabled={loading}>
              Cancel
            </Button>
            <Button variant="success" type="submit" disabled={loading || !hasUserChanges}>
              {loading ? <Spinner as="span" animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default VehicleEdit;